import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchOrders, markItemPicked, completeOrder } from '../store/slices/orderManagementSlice';
import { searchInventory } from '../store/slices/inventorySlice';
import { Order, OrderItem } from '../types/order';
import { 
  Package, 
  MapPin, 
  Search, 
  CheckCircle, 
  Clock, 
  Truck,
  BarChart3,
  User,
  Loader2,
  RefreshCw
} from 'lucide-react';

const OrderPickingPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { orders, loading } = useAppSelector((state) => state.orderManagement);
  const { searchResults, searchLoading } = useAppSelector((state) => state.inventory);
  const { user } = useAppSelector((state) => state.auth);

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);  const [pickingProgress, setPickingProgress] = useState<{ [key: number]: boolean }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showLocationSearch, setShowLocationSearch] = useState(false);

  // Since we're already filtering by worker_id and status in the API call,
  // we can use the orders directly instead of filtering again
  const assignedOrders = orders;
  
  // Enhanced debug logging for development
  useEffect(() => {
    console.log('OrderPicking Debug:');
    console.log('- User ID:', user?.id);
    console.log('- API Base URL: http://localhost:8000/api');
    console.log('- Orders returned from API:', orders.length);
    console.log('- All orders:', orders);
    
    // Log each order's items and their locations
    orders.forEach((order, orderIndex) => {
      console.log(`Order ${orderIndex + 1} (#${order.order_number}):`);
      order.items.forEach((item, itemIndex) => {
        console.log(`  Item ${itemIndex + 1}: ${item.sku} → Location: ${item.location || 'NULL'}`);
      });
    });
  }, [orders, user?.id]);
  useEffect(() => {
    // Load assigned orders initially
    dispatch(fetchOrders({
      status: 'assigned,picking',
      worker_id: user?.id
    }));

    // Set up periodic refresh to check for newly assigned orders
    const interval = setInterval(() => {
      if (user?.id) {
        dispatch(fetchOrders({
          status: 'assigned,picking',
          worker_id: user?.id
        }));
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [dispatch, user?.id]);

  // Also listen for focus events to refresh when user returns to the page
  useEffect(() => {
    const handleFocus = () => {
      if (user?.id && document.visibilityState === 'visible') {
        dispatch(fetchOrders({
          status: 'assigned,picking',
          worker_id: user?.id
        }));
      }
    };

    document.addEventListener('visibilitychange', handleFocus);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('focus', handleFocus);
    };
  }, [dispatch, user?.id]);

  const handleOrderSelect = (order: Order) => {
    setSelectedOrder(order);
    // Initialize picking progress
    const progress: { [key: number]: boolean } = {};
    order.items.forEach(item => {
      progress[item.id] = item.status === 'picked';
    });
    setPickingProgress(progress);
  };  const handleItemPick = async (item: OrderItem) => {
    if (!selectedOrder) return;

    try {
      // Call API to mark item as picked
      await dispatch(markItemPicked({ 
        orderId: selectedOrder.id, 
        itemId: item.id,
        location: item.location,
        notes: `Picked from location ${item.location || 'unknown'}`
      })).unwrap();

      // Update local state
      setPickingProgress(prev => ({
        ...prev,
        [item.id]: true
      }));

      console.log('✅ Item marked as picked via API:', item);    } catch (error) {
      console.error('❌ Failed to mark item as picked via API:', error);
      // Re-throw the error to show proper error handling to the user
      throw error;
    }
  };

  const handleCompleteOrder = async () => {
    if (!selectedOrder) return;

    try {
      // Complete the order
      await dispatch(completeOrder(selectedOrder.id)).unwrap();
      
      // Refresh orders to show updated status
      dispatch(fetchOrders({
        status: 'assigned,picking',
        worker_id: user?.id
      }));

      // Clear selected order since it's now completed
      setSelectedOrder(null);
      setPickingProgress({});

      console.log('Order completed:', selectedOrder.order_number);    } catch (error) {
      console.error('Failed to complete order:', error);
      
      // Fallback: Show success message and clear local state
      // This ensures the UI works during development
      if (error instanceof Error && error.message.includes('404')) {
        console.log('Complete order endpoint not available, showing local completion');
        alert(`Order ${selectedOrder.order_number} marked as complete (local only - backend endpoint needed)`);
        
        // Clear selected order
        setSelectedOrder(null);
        setPickingProgress({});
        
        // Refresh orders
        dispatch(fetchOrders({
          status: 'assigned,picking',
          worker_id: user?.id
        }));
      }
    }
  };

  const handleLocationSearch = () => {
    if (searchQuery.trim()) {
      const [styleCode, color, size] = searchQuery.split('-');
      dispatch(searchInventory({
        style_code: styleCode || searchQuery,
        color: color || undefined,
        size: size || undefined
      }));
    }
  };

  const calculateOrderProgress = (order: Order) => {
    const totalItems = order.items.length;
    const pickedItems = order.items.filter(item => 
      item.status === 'picked' || pickingProgress[item.id]
    ).length;
    return Math.round((pickedItems / totalItems) * 100);
  };

  const getOrderStatusBadge = (status: string) => {
    const statusConfig = {
      assigned: { color: 'bg-blue-100 text-blue-800', label: 'Assigned' },
      picking: { color: 'bg-yellow-100 text-yellow-800', label: 'In Progress' },
      picked: { color: 'bg-green-100 text-green-800', label: 'Completed' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.assigned;
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };
  const handleRefreshOrders = () => {
    if (user?.id) {
      dispatch(fetchOrders({
        status: 'assigned,picking',
        worker_id: user?.id
      }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Order Picking</h1>
          <p className="text-gray-600">Pick items for assigned orders</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            <User className="h-4 w-4 inline mr-1" />
            {user?.username}
          </div>
          <button
            onClick={handleRefreshOrders}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Orders
          </button>
          <button
            onClick={() => setShowLocationSearch(!showLocationSearch)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          >
            <Search className="h-4 w-4 mr-2" />
            Location Search
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-gray-900">{assignedOrders.length}</p>
              <p className="text-sm text-gray-600">Assigned Orders</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-gray-900">
                {assignedOrders.filter(o => o.status === 'picking').length}
              </p>
              <p className="text-sm text-gray-600">In Progress</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-gray-900">
                {assignedOrders.reduce((sum, order) => 
                  sum + order.items.filter(item => item.status === 'picked' || pickingProgress[item.id]).length, 0
                )}
              </p>
              <p className="text-sm text-gray-600">Items Picked</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-gray-900">
                {assignedOrders.length > 0 
                  ? Math.round(assignedOrders.reduce((sum, order) => sum + calculateOrderProgress(order), 0) / assignedOrders.length)
                  : 0}%
              </p>
              <p className="text-sm text-gray-600">Avg Progress</p>
            </div>
          </div>
        </div>
      </div>

      {/* Location Search Panel */}
      {showLocationSearch && (
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Find Item Location</h3>
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Enter SKU (e.g., GFW23669-UK8-Black) or Style Code"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch()}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleLocationSearch}
              disabled={!searchQuery.trim() || searchLoading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {searchLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Search
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="font-medium text-gray-900">Locations Found:</h4>
              {searchResults.map((item, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded-md">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{item.sku}</p>
                      <p className="text-sm text-gray-600">{item.style_name}</p>
                    </div>
                    <span className="text-sm font-medium text-gray-900">Qty: {item.quantity}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {item.locations.map((location, locIndex) => (
                      <div key={locIndex} className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                        <MapPin className="h-3 w-3 mr-1" />
                        {location.location} ({location.quantity})
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Assigned Orders</h2>
          </div>
          
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
                <p className="text-gray-600 mt-2">Loading orders...</p>
              </div>
            ) : assignedOrders.length === 0 ? (
              <div className="p-8 text-center">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Assigned</h3>
                <p className="text-gray-600">You don't have any orders assigned for picking.</p>
              </div>
            ) : (
              assignedOrders.map(order => {
                const progress = calculateOrderProgress(order);
                return (
                  <div
                    key={order.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedOrder?.id === order.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                    onClick={() => handleOrderSelect(order)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium text-gray-900">Order #{order.order_number}</h3>
                        <p className="text-sm text-gray-600">{order.total_items} items</p>
                      </div>
                      {getOrderStatusBadge(order.status)}
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Progress: {progress}%</span>
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Order Details & Picking */}
        <div className="bg-white rounded-lg shadow">
          {selectedOrder ? (
            <>
              <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">Order #{selectedOrder.order_number}</h2>
                    <p className="text-sm text-gray-600">
                      {selectedOrder.items.length} items • Created {new Date(selectedOrder.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {getOrderStatusBadge(selectedOrder.status)}
                </div>
              </div>
                <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {selectedOrder.items.map(item => {
                  const isPicked = item.status === 'picked' || pickingProgress[item.id];
                  
                  // Debug logging to track location data flow
                  console.log(`Item ${item.sku} location debug:`, {
                    location: item.location,
                    hasLocation: !!item.location,
                    type: typeof item.location
                  });
                  
                  return (
                    <div key={item.id} className={`p-4 ${isPicked ? 'bg-green-50' : ''}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{item.sku}</h4>
                          <p className="text-sm text-gray-600">{item.style_name}</p>
                          <div className="flex gap-4 text-sm text-gray-500 mt-1">
                            <span>Size: {item.size}</span>
                            <span>Color: {item.color}</span>
                            <span>Qty: {item.quantity}</span>
                          </div>
                          
                          {/* Enhanced Location Display */}
                          {item.location ? (
                            <div className="flex items-center mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                              <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                              <div>
                                <span className="text-sm font-medium text-blue-900">Location: </span>
                                <span className="text-sm font-bold text-blue-700">{item.location}</span>
                                {item.location_type && (
                                  <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-1 py-0.5 rounded">
                                    {item.location_type}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                              <MapPin className="h-4 w-4 mr-2 text-amber-600" />
                              <div>
                                <span className="text-sm text-amber-700">Location not assigned</span>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="ml-4">
                          {isPicked ? (
                            <div className="flex items-center text-green-600">
                              <CheckCircle className="h-5 w-5 mr-1" />
                              <span className="text-sm font-medium">Picked</span>
                            </div>                          ) : (
                            <button
                              onClick={() => handleItemPick(item)}
                              disabled={loading}
                              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {loading ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                  Picking...
                                </>
                              ) : (
                                <>
                                  <Package className="h-4 w-4 mr-1" />
                                  Pick
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Order Actions */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {selectedOrder.items.filter(item => item.status === 'picked' || pickingProgress[item.id]).length} of {selectedOrder.items.length} items picked
                  </div>
                    {calculateOrderProgress(selectedOrder) === 100 && (
                    <button 
                      onClick={handleCompleteOrder}
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Completing...
                        </>
                      ) : (
                        <>
                          <Truck className="h-4 w-4 mr-2" />
                          Complete Order
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Order</h3>
              <p className="text-gray-600">Choose an order from the list to start picking items.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderPickingPage;
